import {Component} from 'react';
import PropTypes from 'prop-types';

let googleMapsPromise = null;
let mapsLoaderPath = null;
const AUTH_FAILED_FLAG = '__ccGmapsAuthFailed';
const LOADER_PATH_KEY = 'cc_gmaps_loader_path';

export const isHomePath = (pathname = window.location.pathname) =>
  pathname === '/' || pathname === '';

const getMapsLoaderPath = () => {
  if (mapsLoaderPath) {
    return mapsLoaderPath;
  }
  try {
    return sessionStorage.getItem(LOADER_PATH_KEY);
  } catch (error) {
    return null;
  }
};

const setMapsLoaderPath = pathname => {
  mapsLoaderPath = pathname;
  try {
    sessionStorage.setItem(LOADER_PATH_KEY, pathname);
  } catch (error) {}
};

const isGoogleMapsReady = google => {
  try {
    if (!google || !google.maps || typeof google.maps.Map !== 'function') {
      return false;
    }
    const bounds = new google.maps.LatLngBounds();
    return Boolean(bounds && typeof bounds.extend === 'function');
  } catch (error) {
    return false;
  }
};

const registerAuthFailureHandler = () => {
  const previous = window.gm_authFailure;
  window.gm_authFailure = () => {
    window[AUTH_FAILED_FLAG] = true;
    if (typeof previous === 'function') {
      previous();
    }
  };
};

const shouldReloadHomeToRecoverMaps = () => {
  if (!isHomePath()) {
    return false;
  }
  const storedPath = getMapsLoaderPath();
  return Boolean(storedPath && !isHomePath(storedPath));
};

export const loadGoogleMapsApi = apiKey => {
  if (isGoogleMapsReady(window.google) && !window[AUTH_FAILED_FLAG]) {
    return Promise.resolve(window.google);
  }

  if (googleMapsPromise) {
    return googleMapsPromise;
  }

  registerAuthFailureHandler();

  googleMapsPromise = new Promise((resolve, reject) => {
    const callbackName = '__construcaribeGoogleMapsInit';
    window[callbackName] = () => {
      if (isGoogleMapsReady(window.google) && !window[AUTH_FAILED_FLAG]) {
        resolve(window.google);
        return;
      }
      googleMapsPromise = null;
      reject(new Error('Google Maps did not initialize'));
    };

    const existing = document.querySelector(
      'script[data-construcaribe-maps="true"]',
    );
    if (existing) {
      if (isGoogleMapsReady(window.google) && !window[AUTH_FAILED_FLAG]) {
        resolve(window.google);
      }
      return;
    }

    const script = document.createElement('script');
    script.dataset.construcaribeMaps = 'true';
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      apiKey,
    )}&v=weekly&language=es&region=CO&loading=async&callback=${callbackName}`;
    script.onerror = () => {
      googleMapsPromise = null;
      reject(new Error('Google Maps script failed to load'));
    };
    document.head.appendChild(script);
  });

  return googleMapsPromise;
};

class GoogleMapsApiLoader extends Component {
  static propTypes = {
    apiKey: PropTypes.string.isRequired,
    children: PropTypes.func.isRequired,
  };

  state = {
    google: null,
  };

  mounted = true;

  componentDidMount() {
    if (shouldReloadHomeToRecoverMaps()) {
      window[AUTH_FAILED_FLAG] = false;
      setMapsLoaderPath('/');
      window.location.reload();
      return;
    }

    setMapsLoaderPath(window.location.pathname);

    if (isGoogleMapsReady(window.google) && !window[AUTH_FAILED_FLAG]) {
      this.setState({google: window.google});
      return;
    }

    loadGoogleMapsApi(this.props.apiKey)
      .then(google => {
        if (this.mounted && !window[AUTH_FAILED_FLAG]) {
          this.setState({google});
        }
      })
      .catch(() => {
        if (this.mounted) {
          this.setState({google: null});
        }
      });
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  render() {
    const {google} = this.state;
    if (!isGoogleMapsReady(google) || window[AUTH_FAILED_FLAG]) {
      return null;
    }
    return this.props.children(google);
  }
}

export default GoogleMapsApiLoader;
